import { memo } from 'react';
import clsx from 'clsx';
import { ComponentMemo } from '@headless-workspace/typings';
import { cva, VariantProps } from 'class-variance-authority';
import { GlowIcon, GlowTextBodySmall, GlowTextCaption, IconType, PropsWithFontFamily } from '../../../../src';
import { PropsWithTestId } from '../../props';
import { getIcon, IconName } from '../../res/icons';

export type TagSize = 'small' | 'medium' | 'large';
export type TagColor = VariantProps<typeof typeStyles>['color'];

type FontSize = 'caption' | 'bodySmall';

const typeStyles = cva('flex items-center gap-0.25 w-fit', {
    variants: {
        color: {
            primaryRed: 'bg-background-error-red text-text-error',
            primaryGrey: 'bg-background-tertiary text-text-primary',
            primaryBlue: 'bg-background-infos text-text-infos',
            primaryBlack: 'bg-background-inverse text-text-primary-inverse',
            primaryOrange: 'bg-background-warning text-text-warning',
            primaryGreen: 'bg-background-success text-text-success',

            secondaryRed: 'bg-background-error-red-secondary text-text-error-secondary',
            secondaryGrey: 'bg-surface-brand-hover text-text-primary-inverse',
            secondaryBlue: 'bg-background-infos-secondary text-text-infos-secondary',
            secondaryOrange: 'bg-background-warning-secondary text-text-warning-secondary',
            secondaryGreen: 'bg-background-success-secondary text-text-success-secondary',
        },
        size: {
            small: 'px-0.25',
            medium: 'p-0.25',
            large: 'px-0.5 py-0.375',
        },
        shape: {
            standalone: 'rounded-0.25',
            left: 'rounded-l-0.25 rounded-r-0',
        },
        stretch: {
            false: '',
            true: 'self-stretch',
        },
    },
    defaultVariants: {
        color: 'primaryBlack',
        size: 'large',
        shape: 'standalone',
        stretch: false,
    },
});

const sizeStyles: Record<TagSize, { fontSize: FontSize; iconSize?: IconType }> = {
    small: {
        fontSize: 'caption',
    },
    medium: {
        fontSize: 'caption',
        iconSize: 'small',
    },
    large: {
        fontSize: 'bodySmall',
        iconSize: 'medium',
    },
};

export type GlowTagProps = VariantProps<typeof typeStyles> &
    Partial<PropsWithFontFamily> &
    PropsWithTestId & {
        label: string;
    } & ({ size: Exclude<TagSize, 'small'>; iconName?: IconName } | { size: Extract<TagSize, 'small'> });

/**
 * Component called Tag on FIGMA
 * This component renders a tag with customizable label, type, size, and optional icon.
 *
 * @component
 * @param {GlowTagProps} props - The properties object for the GlowTag component.
 * @param {string} props.label - The text label to be displayed within the tag.
 * @param {TagColor} props.color - The color type of the tag, which determines its background and text colors.
 * Possible values include "primaryRed", "primaryGrey", "primaryBlue", "primaryBlack", "primaryOrange", "primaryGreen",
 * "secondaryRed", "secondaryGrey", "secondaryBlue", "secondaryOrange", and "secondaryGreen".
 * @param {TagSize} props.size - The size of the tag, which affects the font size and padding.
 * Possible values include "small", "medium", and "large".
 * @param {IconName} [props.iconName] - The name of the icon to be displayed within the tag.
 * This is optional and will only be displayed if provided and the size is not "small".
 * @param {string} [props.fontFamily] - The font family to be used for the tag's text. Can be 'brand' or 'arial'.
 *
 * @returns {JSX.Element} A JSX element representing the styled tag.
 *
 * @example
 * <GlowTag label={'New'} color={'primaryGreen'} size={'small'} />
 */

export const GlowTag: ComponentMemo<GlowTagProps> = memo(
    ({ label, color, size = 'large', shape, stretch, fontFamily = 'brand', dataTestId, ...props }) => {
        const { fontSize, iconSize } = sizeStyles[size];

        const LabelComponent = size === 'large' ? GlowTextBodySmall : GlowTextCaption;

        return (
            <div className={clsx('flex items-center gap-0.25 w-fit', typeStyles({ color, size, shape, stretch }))} data-testid={dataTestId}>
                {'iconName' in props && <GlowIcon Icon={getIcon(props.iconName)} type={iconSize} />}
                <LabelComponent
                    text={label}
                    fontSize={fontSize}
                    className={'text-inherit'}
                    fontWeight={'bold'}
                    fontFamily={fontFamily}
                />
            </div>
        );
    },
);

GlowTag.displayName = 'GlowTag';